import React, {
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import BigNumber from 'bignumber.js';
import { useTradeSetting } from '../../../../../hooks/trade/tradeSetting';
import useTradeStore from '../../../../../hooks/trade/tradeStore';
import { useBalance } from '../../../../../hooks/user/mybalance';

import { Slider, message } from 'antd';
import { ThemeContext } from '../../../../../../theme/ThemeProvider';
import { useIndexPrice } from '../../../../../hooks/trade/indexprice';
import { useTradeOrder } from '../../../../../hooks/trade/tradeOrder';
import { useTradePosition } from '../../../../../hooks/trade/tradePosition';
import { useMyProfile } from '../../../../../hooks/user/myprofile';
import useGetCoinTier from '../../../../../hooks/trade/coinTier';
// import usePercentageStore from "../../../../../hooks/trade/percentageStore"; // zustand store import 추가
import usePercentageStore from '@/hooks/trade/percentageStore';
import { useTranslation } from 'react-i18next';

const PercentageForm = ({
  percentage,
  setPercentage,
  isManualInputRef,
}: any) => {
  const { COLORS } = useContext(ThemeContext).theme;
  const { t } = useTranslation();

  const { data: store, mutate: mutateStore } = useTradeStore();
  const { data: settings } = useTradeSetting(store?.ep_id);

  const { fee } = useMyProfile();

  const maxfee = Math.max(fee?.maker_fee ?? 0.01, fee?.taker_fee ?? 0.01);

  const { data: position } = useTradePosition();

  const { data: orders } = useTradeOrder(store?.ep_id);

  const currency = store?.pairs?.split('/')[1];
  const { data: balance } = useBalance(currency, 1);

  const { getPrice } = useIndexPrice(store?.ep_id || 0);
  const { getMaxNotionalForLeverage } = useGetCoinTier();

  const currentEPPos = position?.find(
    (item) => +item.exchange_pairs_id == +store?.ep_id
  );

  const availableBalance = new BigNumber(balance?.balance || 0)
    .minus(balance?.lockbalance || 0)
    .toNumber();

  let ordermarinsum = 0;

  if (orders && orders.length > 0) {
    orders.map((item) => {
      if (item?.reduce_position == 0) {
        ordermarinsum += item?.margin;
      }
    });
  }

  let calcprice = store?.order_price;
  if (store?.limit_market === 'M') {
    calcprice = getPrice(store?.ep_id);
  }

  // maxAmount 계산 함수
  const calculateMaxAmount = (useLatestPrice = false) => {
    const priceToUse =
      useLatestPrice && store?.limit_market === 'M'
        ? getPrice(store?.ep_id)
        : calcprice;

    let maxAmount = BigNumber(availableBalance - ordermarinsum);

    if (store?.main_sub != 'S') {
      maxAmount = maxAmount.multipliedBy(
        BigNumber(settings?.leverage).dividedBy(priceToUse)
      );
    } else {
      maxAmount = maxAmount.multipliedBy(BigNumber(settings?.leverage));
    }

    const feeprice = BigNumber(store?.order_amount || 1)
      .multipliedBy(maxfee || 1)
      .dividedBy(100)
      .multipliedBy(settings?.leverage);

    maxAmount = maxAmount
      .minus(feeprice)
      .minus(store?.main_sub !== 'S' ? 0.00001 : 2);

    // 코인 티어표를 기준으로 한 레버리지별 수량 제한 적용
    const coinSymbol = store?.pairs?.split('/')[0];

    if (coinSymbol && priceToUse) {
      const currentLeverage = settings?.leverage || 1;
      const maxNotional = getMaxNotionalForLeverage(
        coinSymbol,
        currentLeverage
      );

      if (maxNotional && maxNotional !== Infinity) {
        if (store?.main_sub === 'S') {
          const maxAllowedValue = new BigNumber(maxNotional);
          maxAmount = BigNumber.minimum(maxAmount, maxAllowedValue);
        } else {
          const maxAllowedAmount = new BigNumber(maxNotional).dividedBy(
            priceToUse
          );
          maxAmount = BigNumber.minimum(maxAmount, maxAllowedAmount);
        }
      }
    }

    // 포지션을 고려한 maxAmount 조정
    if (currentEPPos && currentEPPos.margin > 0) {
      let currentPositionAmount;

      if (store?.main_sub === 'S') {
        currentPositionAmount = Math.abs(
          currentEPPos.amount * priceToUse * (settings?.leverage || 1)
        );
      } else {
        currentPositionAmount = Math.abs(
          currentEPPos.amount * (settings?.leverage || 1)
        );
      }

      const selectedDirection = store?.long_short;

      if (currentEPPos.long_short === 'L') {
        if (selectedDirection === 'L') {
          maxAmount = BigNumber.maximum(
            maxAmount.minus(currentPositionAmount),
            0
          );
        } else if (selectedDirection === 'S') {
          maxAmount = maxAmount.plus(currentPositionAmount);
        }
      } else {
        if (selectedDirection === 'L') {
          maxAmount = maxAmount.plus(currentPositionAmount);
        } else if (selectedDirection === 'S') {
          maxAmount = BigNumber.maximum(
            maxAmount.minus(currentPositionAmount),
            0
          );
        }
      }
    }

    return maxAmount;
  };

  const maxAmount = calculateMaxAmount();

  const prevCalculatedAmount = useRef<number | null>(null);
  const prevMarketPrice = useRef<number | null>(null);

  // 시장가에서 가격 변화 시 퍼센트 유지하면서 수량 재계산
  useEffect(() => {
    // 시장가가 아니면 실행하지 않음
    if (store?.limit_market !== 'M') return;
    if (isManualInputRef.current) return;
    if (!percentage || percentage === '0') return;

    const currentMarketPrice = getPrice(store?.ep_id);

    // 가격이 변경되었고, 이전 가격이 있을 때만 수량 재계산
    if (
      currentMarketPrice &&
      prevMarketPrice.current !== null &&
      prevMarketPrice.current !== currentMarketPrice
    ) {
      // 상위 컴포넌트의 percentage 재계산을 방지하기 위해 수동 입력 플래그 설정
      isManualInputRef.current = true;

      const calculatedAmount = Math.abs(
        +maxAmount.multipliedBy(percentage).dividedBy(100).toFixed(4)
      );

      // console.log("calculatedAmount", calculatedAmount, +maxAmount, percentage);

      mutateStore({
        ...store,
        order_amount: calculatedAmount,
      });

      // 짧은 시간 후 플래그 해제
      setTimeout(() => {
        isManualInputRef.current = false;
      }, 100);
    }

    prevMarketPrice.current = currentMarketPrice;
  }, [calcprice]);

  useEffect(() => {
    if (!currentEPPos || currentEPPos.margin <= 0) return;
    if (isManualInputRef.current) return;

    const calculatedAmount = Math.abs(
      +maxAmount.multipliedBy(percentage).dividedBy(100).toFixed(4)
    );

    // console.log("calculatedAmount", store);

    if (prevCalculatedAmount.current !== calculatedAmount) {
      prevCalculatedAmount.current = calculatedAmount;
      mutateStore({
        ...store,
        order_amount: calculatedAmount,
      });
    }
  }, [
    store?.order_price,
    store?.reduce_only,
    settings?.leverage,
    currentEPPos?.margin,
    store?.pairs,
  ]);

  // useEffect(() => {
  //   if (!currentEPPos || currentEPPos.margin <= 0) {
  //     return;
  //   }
  //
  //   const calculatedAmount = Math.max(
  //     +maxAmount.multipliedBy(+percentage).dividedBy(100).toFixed(5),
  //     0,
  //   );
  //
  //   if (store?.order_amount !== calculatedAmount) {
  //     mutateStore({
  //       ...store,
  //       order_amount: calculatedAmount,
  //     });
  //   }
  // }, [
  //   store?.order_price,
  //   store?.reduce_only,
  //   settings?.leverage,
  //   currentEPPos
  // ]);

  // console.log("PercentageForm", percentage);

  // useEffect(() => {
  //   console.log('percentage', percentage)
  // }, [percentage]);

  const setGlobalPercentage = usePercentageStore(
    (state) => state.setPercentage
  ); // zustand setter

  // setTimeout 참조를 저장할 ref 추가
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // marks 객체를 메모이제이션 (COLORS.textw가 변경될 때만 재생성)
  const sliderMarks = useMemo(
    () => ({
      0: {
        style: { color: COLORS.textw },
        label: (
          <div
            style={{
              fontSize: 12,
              color: '#B3B5BD',
              fontFamily: 'NanumSquareRound',
              letterSpacing: -0.36,
              position: 'relative',
              left: 'calc(50% - 4px)',
              top: '4px',
            }}
          >
            0%
          </div>
        ),
      },
      25: {
        style: { color: COLORS.textw },
        label: <strong style={{ fontSize: 11 }}>{''}</strong>,
      },
      50: {
        style: { color: COLORS.textw },
        label: <strong style={{ fontSize: 11 }}>{''}</strong>,
      },
      75: {
        style: { color: COLORS.textw },
        label: <strong style={{ fontSize: 11 }}>{''}</strong>,
      },
      100: {
        style: { color: COLORS.textw },
        label: (
          <div
            style={{
              fontSize: 12,
              color: '#B3B5BD',
              fontFamily: 'NanumSquareRound',
              letterSpacing: -0.36,
              position: 'relative',
              right: 'calc(50% - 4px)',
              top: '4px',
            }}
          >
            100%
          </div>
        ),
      },
    }),
    [COLORS.textw]
  );

  // 잔액이 없을 때 슬라이더 비활성화 여부 체크
  const isSliderDisabled = availableBalance <= 0;

  // onChange 핸들러를 useCallback으로 메모이제이션
  const handleSliderChange = useCallback(
    (value: number) => {
      // 잔액이 없으면 동작하지 않음
      if (isSliderDisabled) {
        return;
      }

      // console.log('onChange', value);

      // 이전 setTimeout 정리
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setPercentage(value);
      setGlobalPercentage(value);

      // 실시간으로 수량도 업데이트 (최신 가격 사용)
      const currentMaxAmount = calculateMaxAmount(true);
      const calculatedAmount = Math.abs(
        +currentMaxAmount.multipliedBy(value).dividedBy(100).toFixed(4)
      );

      mutateStore({
        ...store,
        order_amount: calculatedAmount,
      });

      // 새로운 setTimeout 설정
      timeoutRef.current = setTimeout(() => {
        isManualInputRef.current = false;
        timeoutRef.current = null; // 참조 정리
      }, 150);
    },
    [
      isSliderDisabled,
      setPercentage,
      setGlobalPercentage,
      calculateMaxAmount,
      store,
      mutateStore,
      isManualInputRef,
    ]
  );

  // 컴포넌트 언마운트 시 setTimeout 정리
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        boxSizing: 'border-box',
        padding: '12px 17px 0 14px',
        marginBottom: '27px',
        width: '100%',
      }}
    >
      <Slider
        marks={sliderMarks}
        styles={{
          rail: {
            background: '#2A2C2E',
            height: 3,
          },
          track: {
            background: '#07D993',
            height: 3,
          },
          handle: {
            border: `1px solid #07D993`,
            color: 'black',
            background: '#07D993',
          },
        }}
        value={percentage}
        onChange={handleSliderChange}
        disabled={isSliderDisabled}
        // onChangeComplete={(value) => {
        //   console.log('onChangeComplete', value);
        //   // 최신 가격으로 maxAmount 계산하여 수량 업데이트
        //   const currentMaxAmount = calculateMaxAmount(true);

        //   const calculatedAmount = Math.abs(
        //     +currentMaxAmount.multipliedBy(value).dividedBy(100).toFixed(4)
        //   );

        //   // const calculatedAmount = Math.max(
        //   //   +currentMaxAmount.multipliedBy(+value).dividedBy(100).toFixed(5),
        //   //   0
        //   // );

        //   mutateStore({
        //     ...store,
        //     order_amount: calculatedAmount,
        //   });

        //   setTimeout(() => {
        //     isManualInputRef.current = false;
        //   }, 1000);
        // }}
      />
      {/* disabled 상태일 때만 보이는 투명 오버레이 - 클릭 이벤트 충돌 방지 */}
      {isSliderDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            cursor: 'not-allowed',
            zIndex: 1,
          }}
          onClick={(e) => {
            e.stopPropagation();
            message.error(t('insufficientBalance') || '잔액이 부족합니다');
          }}
        />
      )}
    </div>
  );
};

export default PercentageForm;
